import React, { useState } from 'react';
import PropTypes from 'prop-types';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import { SingleSelect } from '@els/els-react--select';
import { RadioButtonGroup } from '@els/els-react--radio-button';
import { cellOperationOptionsMapping, showSubstancesOptionsMapping } from '../constants';

import './HeatmapSettings.custom.scss';

const mapCellOperationToOption = (cellOperation) => {
  return { label: cellOperationOptionsMapping[cellOperation], value: cellOperation };
};
const mapCellOperationToOptions = (settingsResponse) => {
  const { cellOperations } = settingsResponse;
  return cellOperations.map(mapCellOperationToOption);
};

function HeatmapSettingsDialogContent({ isLoading, settingsResponse, currentSettings, updateCurrentSetting }) {
  const selectOptionCol = currentSettings.col?.value || '';
  const selectOptionRow = currentSettings.row?.value || '';
  const selectOptionCellOperation = mapCellOperationToOption(currentSettings.cellOperation)?.value || '';

  return (
    <section className="heatmap-settings-dialog__content">
      {isLoading ? (
        <SimpleSpinner />
      ) : (
        settingsResponse &&
        currentSettings && (
          <>
            <div className="heatmap-settings-dialog__row">
              <div className="heatmap-settings-dialog__row-label">Value of X-axis</div>
              <SingleSelect
                label="Value of X-axis"
                shouldHideLabel
                size="small"
                className="heatmap-settings-dialog__row-options heatmap-settings-dialog__select"
                e2eName="heatmap-settings-value-of-x-axis"
                optionsFlyoutProps={{
                  noInternalPadding: true,
                  theme: 'default',
                  placement: 'bottom',
                  pointer: true,
                  pointerType: 'primary',
                }}
                options={settingsResponse.cols.map((col) => ({
                  value: col.id ?? col.value ?? col,
                  label: col.title ?? col.label ?? String(col),
                  title: col.title ?? col.label ?? String(col),
                }))}
                selectedOptions={[selectOptionCol]}
                onSelect={(option) => updateCurrentSetting('col', option)}
                required
              />
            </div>
            <div className="heatmap-settings-dialog__row">
              <div className="heatmap-settings-dialog__row-label">Value of Y-axis</div>
              <SingleSelect
                label="Value of Y-axis"
                shouldHideLabel
                size="small"
                className="heatmap-settings-dialog__row-options heatmap-settings-dialog__select"
                e2eName="heatmap-settings-value-of-y-axis"
                optionsFlyoutProps={{
                  noInternalPadding: true,
                  theme: 'default',
                  placement: 'bottom',
                  pointer: true,
                  pointerType: 'primary',
                }}
                options={settingsResponse.rows.map((row) => ({
                  value: row.id ?? row.value ?? row,
                  label: row.title ?? row.label ?? String(row),
                  title: row.title ?? row.label ?? String(row),
                }))}
                selectedOptions={[selectOptionRow]}
                onSelect={(option) => updateCurrentSetting('row', option)}
                required
              />
            </div>
            <div className="heatmap-settings-dialog__row">
              <div className="heatmap-settings-dialog__row-label">Value of Cells</div>
              <SingleSelect
                label="Value of Cells"
                shouldHideLabel
                size="small"
                className="heatmap-settings-dialog__row-options heatmap-settings-dialog__select"
                e2eName="heatmap-settings-value-of-cells"
                optionsFlyoutProps={{
                  noInternalPadding: true,
                  theme: 'default',
                  placement: 'bottom',
                  pointer: true,
                  pointerType: 'primary',
                }}
                options={mapCellOperationToOptions(settingsResponse)}
                selectedOptions={[selectOptionCellOperation]}
                onSelect={(option) => updateCurrentSetting('cellOperation', option.value)}
                required
              />
            </div>
            <div className="heatmap-settings-dialog__row">
              <div className="heatmap-settings-dialog__row-label">Show substances</div>
              <div className="heatmap-settings-dialog__row-options heatmap-settings-dialog__radio-buttons-group heatmap-settings-dialog__radio-buttons-group--vertical">
                <RadioButtonGroup.Option
                  className="heatmap-settings-dialog__radio-button"
                  onChange={() => updateCurrentSetting('showSubstances', 'names')}
                  checked={currentSettings.showSubstances === 'names'}
                  label={showSubstancesOptionsMapping.names}
                />
                <RadioButtonGroup.Option
                  className="heatmap-settings-dialog__radio-button"
                  labelClassName="heatmap-settings-dialog__radio-button-label"
                  onChange={() => updateCurrentSetting('showSubstances', 'drawing')}
                  checked={currentSettings.showSubstances === 'drawing'}
                  label={showSubstancesOptionsMapping.drawing}
                />
              </div>
            </div>
            <div className="heatmap-settings-dialog__row">
              <div className="heatmap-settings-dialog__row-label">Display mode</div>
              <div className="heatmap-settings-dialog__row-options heatmap-settings-dialog__radio-buttons-group heatmap-settings-dialog__radio-buttons-group--horizontal">
                <RadioButtonGroup.Option
                  className="heatmap-settings-dialog__radio-button"
                  onChange={() => updateCurrentSetting('startInFullScreen', false)}
                  checked={!currentSettings.startInFullScreen}
                  label="Normal"
                />
                <RadioButtonGroup.Option
                  className="heatmap-settings-dialog__radio-button"
                  labelClassName="heatmap-settings-dialog__radio-button-label"
                  onChange={() => updateCurrentSetting('startInFullScreen', true)}
                  checked={currentSettings.startInFullScreen}
                  label="Full screen"
                />
              </div>
            </div>
          </>
        )
      )}
    </section>
  );
}

HeatmapSettingsDialogContent.defaultProps = {
  updateCurrentSetting: () => {},
};

HeatmapSettingsDialogContent.propTypes = {
  isLoading: PropTypes.bool,
  settingsResponse: PropTypes.object,
  currentSettings: PropTypes.object,
  updateCurrentSetting: PropTypes.func,
};

export default HeatmapSettingsDialogContent;
